import React, { useEffect, useState } from 'react';
import AppLayout from '../../../components/layout/AppLayout';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { TCourse, TChapter } from '../../../models/courses/course';
import { Button, Input, message, Space, Table } from 'antd';
import { SaveOutlined, EyeOutlined, EditOutlined, StopOutlined, DeleteOutlined } from '@ant-design/icons';
import { useCourseApi } from '../../../api/courses/courses';
import type { ColumnsType } from 'antd/es/table';  // Import ColumnsType
import ConfirmationDialog from '../../../components/layout/ConfirmationDialog';
import { CODIFICATION_ROLES } from '../../../constant/codification';
import { useTilmiAuth } from '../../../hooks/useTilmiAuth';

const ChapterList: React.FC = () => {
    const { t } = useTranslation();
    const { isRoleExist } = useTilmiAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const { getChaptersByCourseId, updateChapter, createChapter, deleteChapter } = useCourseApi(); // Assumed API for Chapters
    const [chapters, setChapters] = useState<TChapter[]>([]);
    const [loading, setIsLoading] = useState<boolean>(false);
    const [addingId, setAddingId] = useState<number>(0);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [deleteId, setDeleteId] = useState<number | null>(null);
    const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState<boolean>(false);


    const course: TCourse = location.state?.course;

    useEffect(() => {
        if (!course) {
            message.error('No course selected');
            return;
        }

        setIsLoading(true);
        getChaptersByCourseId(course.id)
            .then((resp) => {
                setChapters(resp.data);
            })
            .catch((err) => {
                message.error(err.response.data ?? err.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
        // eslint-disable-next-line
    }, [course]);

    const [editingKey, setEditingKey] = useState<number | null>(null);
    const [editingChapter, setEditingChapter] = useState<TChapter | null>(null);

    // Function to enter edit mode
    const edit = (chapter: TChapter) => {
        setEditingKey(chapter.id);
        setEditingChapter({ ...chapter });
    };

    // Function to handle adding a new chapter
    const handleAddChapter = () => {
        const idRow = addingId - 1;
        const newChapter: TChapter = {
            id: idRow,
            name: '',
            course_id: course.id,  // Attach the course ID
            chapter_order: 0,
            epics: []
        };

        setAddingId(idRow);
        setEditingKey(idRow);
        setEditingChapter(newChapter);
        setChapters((prev) => [
            newChapter,
            ...(prev || []),
        ]);
    };

    // Save changes
    const save = (id: number) => {
        if (editingChapter) {
            (editingChapter.id > 0 ? updateChapter(editingChapter.id, editingChapter) : createChapter(editingChapter))
                .then((resp) => {
                    const chapterResult: TChapter = resp.data;
                    const updatedChapters = chapters.map((chapter) =>
                        chapter.id === id ? chapterResult : chapter
                    );
                    setChapters(updatedChapters);
                    message.success(t('message.savedSuccessfully'));
                })
                .catch((err) => {
                    message.error(err.response.data ?? err.message);
                    console.error(err);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }

        setEditingKey(null);
    };
    const cancel = () => {
        setEditingKey(null);
        setEditingChapter(null);
    }
    // Show chapter details
    const showDetails = (chapter: TChapter) => {
        navigate('/setup/epic', { state: { chapter } });
    };
    // Handle delete action
    const confirmDelete = (id: number) => {
        setDeleteId(id);
        setIsDeleteDialogVisible(true);
    };

    const handleDelete = () => {
        if (deleteId == null) return;
        setDeleteLoading(true);
        deleteChapter(deleteId)
            .then(() => {
                setChapters(chapters.filter((chapter) => chapter.id !== deleteId));
                message.success(t('message.deletedSuccessfully'));
            })
            .catch((err) => {
                message.error(err.response.data ?? err.message);
            })
            .finally(() => {
                setDeleteLoading(false);
                setIsDeleteDialogVisible(false);
                setDeleteId(null);
            });
    };

    // Explicitly type the columns array
    const columns: ColumnsType<TChapter> = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: TChapter) =>
                editingKey === record.id ? (
                    <Input
                        value={editingChapter?.name}
                        onChange={(e) => {
                            if (editingChapter) {
                                setEditingChapter({ ...editingChapter, name: e.target.value });
                            }
                        }}
                    />
                ) : (
                    text
                ),
        },
        {
            title: 'Ordre',
            dataIndex: 'chapter_order',
            key: 'chapter_order',
            width: 90,
            render: (text: number, record: TChapter) =>
                editingKey === record.id ? (
                    <Input
                        type="number"
                        value={editingChapter?.chapter_order}
                        onChange={(e) => {
                            if (editingChapter) {
                                setEditingChapter({ ...editingChapter, chapter_order: parseInt(e.target.value) });
                            }
                        }}
                    />
                ) : (
                    text
                ),
        },
        {
            title: 'Action',
            key: 'actions',
            fixed: 'right' as 'right',  // Fix the column to the right
            width: 180,  // Set fixed width
            render: (_: any, record: TChapter) => (
                <Space size="middle">
                    {editingKey === record.id ? (

                        <>
                            <Button icon={<SaveOutlined />} onClick={() => save(record.id)} />
                            <Button icon={<StopOutlined />} onClick={cancel} />
                        </>
                    ) : (
                        <>
                            <Button icon={<EditOutlined />} onClick={() => edit(record)} />
                            <Button icon={<EyeOutlined />} onClick={() => showDetails(record)} />

                            {
                                isRoleExist(CODIFICATION_ROLES.DELETE_COURSE) && (
                                    <Button
                                        icon={<DeleteOutlined />}
                                        danger
                                        onClick={() => confirmDelete(record.id)}
                                        loading={deleteId === record.id && deleteLoading}
                                    />
                                )
                            }
                        </>
                    )}
                </Space>
            ),
        },
    ];

    const handleBackNavigation = () => {
        navigate(-1);
    };

    return (
        <AppLayout 
            title={`Course : ${course?.title}`} 
            loading={loading}
            onBackNavigation={handleBackNavigation}
            showBackButton={true}
        >
            <Space style={{ marginBottom: 16 }}>
                <Button type="primary" onClick={handleAddChapter}>
                    {t('button.add')}
                </Button>
            </Space>
            <Table<TChapter>  // Add the generic type here
                columns={columns}
                dataSource={chapters}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 20 }}  // Pagination with 10 chapters per page
                scroll={{ x: 500 }}  // Enable horizontal scrolling if needed
            />
            <ConfirmationDialog
                visible={isDeleteDialogVisible}
                onConfirm={handleDelete}
                onCancel={() => setIsDeleteDialogVisible(false)}
                loading={deleteLoading}
                title="Confirm Deletion"
                description="Are you sure you want to delete this chapter? This action is irreversible."
            />
            
        </AppLayout>
    );
};

export default ChapterList;
